import * as React from "react";
import { Loader2 } from "lucide-react";
import { Skeleton } from "@/v2/components/ui/skeleton";
import {
  activeUnderlineStyle,
  tabButtonStyle,
  tabRowStyle,
} from "@/v2/components/underline-tabs";
import type { Channel } from "../../Events/types";
import { filterContent, type ContentFilters } from "../schedulerUtils";
import type { ContentItem, DragPayload } from "../types";
import type { TouchDragHandle } from "../useTouchDragBridge";
import { useInfiniteGrid } from "../useInfiniteGrid";
import type { UploadsState } from "../useUploads";
import type { WhizzardsState, WhizzardTypeFilter } from "../useWhizzards";
import { ChannelFilterDropdown } from "./ChannelFilterDropdown";
import { ContentFilterBar, SearchBox } from "./ContentFilterBar";
import { ContentCard } from "./parts";
import { UploadedLibrary, UploadVideoButton } from "./UploadedLibrary";
import { WhizzardTypeDropdown } from "./WhizzardTypeDropdown";

type LibraryTab = "games" | "whizzards" | "uploads";

/** Mirrors a ContentCard's shape (16:9 thumb, title line, date line) so the
 *  grid doesn't pop from placeholder cards to real ones. */
function ContentCardSkeleton() {
  return (
    <div>
      <Skeleton
        className="v2-skeleton-wave w-full rounded-md"
        style={{ aspectRatio: "16 / 9" }}
      />
      <div style={{ padding: "6px 2px 2px" }}>
        <Skeleton className="v2-skeleton-wave h-3 w-4/5 rounded" />
        <Skeleton
          className="v2-skeleton-wave h-3 w-1/2 rounded"
          style={{ marginTop: 6 }}
        />
        <Skeleton
          className="v2-skeleton-wave h-3 w-1/3 rounded"
          style={{ marginTop: 4 }}
        />
      </div>
    </div>
  );
}

export const DEFAULT_FILTERS: ContentFilters = {
  query: "",
  length: "all",
  league: "all",
  sort: "newest",
};

interface Props {
  content: ContentItem[];
  dragRef: React.MutableRefObject<DragPayload | null>;
  touchDrag?: TouchDragHandle;
  gamesLoading?: boolean;
  hasMoreGames?: boolean;
  loadingMoreGames?: boolean;
  onLoadMoreGames?: () => void;
  /** Broadcast channels for the filter dropdown (same list/shape the /vod
   *  sidebar uses) and the selected value, passed straight through to the
   *  API's `channel` param by useVodGames. The dropdown only renders when
   *  both channels and onChannelFilterChange are given. */
  channels?: Channel[];
  channelFilter?: string;
  onChannelFilterChange?: (next: string) => void;
  /** Raw (un-debounced) search text, reported on every keystroke; the
   *  caller debounces it before sending it to the API (see
   *  ChannelSchedulerPage), same reasoning as onChannelFilterChange. */
  onSearchChange?: (query: string) => void;
  /** The Uploaded tab's data and actions (HTPTV2-89). Without it the
   *  library is games only, with no tab row. */
  uploads?: UploadsState;
  uploadSearch?: string;
  onUploadSearchChange?: (text: string) => void;
  /** The Whizzards tab (condensed games, goal of the day): the feed, its
   *  own search text and its type filter. The channel dropdown is shared
   *  with the Games tab. Without it there is no Whizzards tab. */
  whizzards?: WhizzardsState;
  whizzardSearch?: string;
  onWhizzardSearchChange?: (text: string) => void;
  whizzardType?: WhizzardTypeFilter;
  onWhizzardTypeChange?: (next: WhizzardTypeFilter) => void;
}

export function ContentLibrary({
  content,
  dragRef,
  touchDrag,
  gamesLoading,
  hasMoreGames,
  loadingMoreGames,
  onLoadMoreGames,
  channels,
  channelFilter,
  onChannelFilterChange,
  onSearchChange,
  uploads,
  uploadSearch = "",
  onUploadSearchChange,
  whizzards,
  whizzardSearch = "",
  onWhizzardSearchChange,
  whizzardType = "all",
  onWhizzardTypeChange,
}: Props) {
  const [tab, setTab] = React.useState<LibraryTab>("games");
  const [filters, setFilters] = React.useState<ContentFilters>(DEFAULT_FILTERS);
  // The search text only goes to the API (useVodGames' `search`): the
  // loaded page is already the server's answer, so it is not filtered
  // again here, where a title-only match would hide games the server
  // matched on other fields.
  const items = React.useMemo(
    () => filterContent(content, { ...filters, query: "", type: "game" }),
    [content, filters],
  );

  React.useEffect(() => {
    onSearchChange?.(filters.query);
  }, [filters.query, onSearchChange]);

  const tabs: [LibraryTab, string][] = [["games", "Games"]];
  if (whizzards) tabs.push(["whizzards", "Highlights"]);
  if (uploads) tabs.push(["uploads", "Uploaded"]);

  return (
    <section
      className="rounded-lg border border-border bg-card"
      style={{ padding: 20 }}
      aria-label="Content Library"
    >
      <div>
        <h2
          className="font-bold text-foreground"
          style={{ fontSize: 20, lineHeight: 1.3 }}
        >
          Content Library
        </h2>
        {/* One subtitle for the whole library, whichever tab is open. */}
        <p className="text-sm text-muted-foreground" style={{ marginTop: 4 }}>
          {uploads
            ? // HIGHLIGHTS TAB HIDDEN: back to "Search games and highlights, or
              // upload your own videos." when the Highlights tab returns.
              "Search games or upload your own videos. Drag an item onto the timeline above."
            : "Search for games. Drag an item onto the timeline above."}
        </p>
      </div>
      {/* One filter row for every tab, always on its own line under the
          subtitle, so switching tabs never moves it (beside the title on one
          tab, wrapped under it on another). The tab's own controls come
          first (channel, plus type on Whizzards; Upload video on Uploaded),
          then the search box for whichever tab is open. */}
      <div
        className="flex flex-wrap items-center"
        style={{ gap: 8, marginTop: 16 }}
      >
        {uploads && tab === "uploads" ? (
          <UploadVideoButton onStart={uploads.start} />
        ) : channels && onChannelFilterChange ? (
          <ChannelFilterDropdown
            channels={channels}
            value={channelFilter ?? ""}
            onChange={onChannelFilterChange}
          />
        ) : null}
        {whizzards && tab === "whizzards" ? (
          <WhizzardTypeDropdown
            value={whizzardType}
            onChange={(next) => onWhizzardTypeChange?.(next)}
          />
        ) : null}
        <div style={{ width: 260, maxWidth: "100%" }}>
          {whizzards && tab === "whizzards" ? (
            <SearchBox
              label="Search highlights"
              placeholder="Search by title"
              value={whizzardSearch}
              onChange={(text) => onWhizzardSearchChange?.(text)}
            />
          ) : uploads && tab === "uploads" ? (
            <SearchBox
              label="Search uploads"
              placeholder="Search by title"
              value={uploadSearch}
              onChange={(text) => onUploadSearchChange?.(text)}
            />
          ) : (
            <ContentFilterBar filters={filters} onChange={setFilters} />
          )}
        </div>
      </div>

      {tabs.length > 1 ? (
        <div
          role="tablist"
          aria-label="Content source"
          style={{ ...tabRowStyle, marginTop: 12 }}
        >
          {tabs.map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
              // Uppercased in CSS, not in the text, so the tab's
              // accessible name stays "Games", not "GAMES".
              style={{
                ...tabButtonStyle(tab === key),
                textTransform: "uppercase",
              }}
            >
              {label}
              {tab === key ? (
                <span aria-hidden style={activeUnderlineStyle} />
              ) : null}
            </button>
          ))}
        </div>
      ) : null}

      {uploads && tab === "uploads" ? (
        <UploadedLibrary
          uploads={uploads}
          search={uploadSearch}
          dragRef={dragRef}
          touchDrag={touchDrag}
        />
      ) : whizzards && tab === "whizzards" ? (
        <LibraryGrid
          items={whizzards.items}
          loading={whizzards.isLoading}
          hasMore={whizzards.hasNextPage}
          loadingMore={whizzards.isFetchingNextPage}
          onLoadMore={whizzards.fetchNextPage}
          emptyText="No highlights match your filters."
          dragRef={dragRef}
          touchDrag={touchDrag}
        />
      ) : (
        <LibraryGrid
          items={items}
          loading={gamesLoading}
          hasMore={hasMoreGames}
          loadingMore={loadingMoreGames}
          onLoadMore={onLoadMoreGames}
          emptyText="No content matches your filters."
          dragRef={dragRef}
          touchDrag={touchDrag}
        />
      )}
    </section>
  );
}

interface GridProps {
  items: ContentItem[];
  loading?: boolean;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  emptyText: string;
  dragRef: React.MutableRefObject<DragPayload | null>;
  touchDrag?: TouchDragHandle;
}

/** A tab's card grid: skeletons on first load, then a two-row scroll window
 *  that loads the next page as it scrolls (see useInfiniteGrid). Its own
 *  component so each tab keeps its own scroll window. */
function LibraryGrid({
  items,
  loading,
  hasMore,
  loadingMore,
  onLoadMore,
  emptyText,
  dragRef,
  touchDrag,
}: GridProps) {
  const {
    scrollRef,
    gridRef,
    sentinelRef,
    maxHeight: twoRowCapPx,
  } = useInfiniteGrid({
    count: items.length,
    hasMore: Boolean(hasMore),
    loadingMore: Boolean(loadingMore),
    onLoadMore,
  });

  if (items.length === 0 && loading) {
    return (
      <div
        className="sched-cards"
        style={{ marginTop: 16 }}
        data-testid="content-grid-skeleton"
      >
        {Array.from({ length: 8 }, (_, i) => (
          <ContentCardSkeleton key={i} />
        ))}
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <p
        className="text-sm text-muted-foreground"
        style={{ padding: "32px 0", textAlign: "center" }}
      >
        {emptyText}
      </p>
    );
  }
  return (
    <div
      ref={scrollRef}
      className="overflow-y-auto"
      style={{ marginTop: 16, maxHeight: twoRowCapPx }}
    >
      <div ref={gridRef} className="sched-cards" data-testid="content-grid">
        {items.map((item) => (
          <ContentCard
            key={item.id}
            item={item}
            dragRef={dragRef}
            touchDrag={touchDrag}
          />
        ))}
      </div>

      {hasMore ? (
        <div
          ref={sentinelRef}
          className="flex justify-center"
          style={{ marginTop: 16, height: 32 }}
        >
          {loadingMore ? (
            <Loader2 size={20} className="animate-spin text-muted-foreground" />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
